import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import {
  getCustomerByPhone,
  saveCustomer,
  formatPhone,
  normalizePhone,
  incrementOrderCount
} from '../services/customerService';
import './CheckoutPage.css';

const CheckoutPage = () => {
  const { cartItems, getTotalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phone: '',
    name: '',
    address: '',
    number: '',
    neighborhood: '',
    reference: '',
    paymentMethod: '',
    deliveryOption: 'delivery'
  });
  const [paymentError, setPaymentError] = useState(false);
  const [highlightPayment, setHighlightPayment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);
  const whatsappInputRef = useRef(null);

  // Formatar preço em reais
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Focar no input de WhatsApp quando o componente montar
  useEffect(() => {
    if (whatsappInputRef.current) {
      whatsappInputRef.current.focus();
    }
  }, []);

  // Manipular mudanças no formulário
  const handleChange = async (e) => {
    const { name, value } = e.target;

    // Se estiver alterando o telefone, formatar o número
    if (name === 'phone') {
      // Limitar a 15 caracteres para evitar números muito longos
      const normalizedValue = normalizePhone(value);
      if (normalizedValue.length > 11) return;

      // Formatar o telefone para exibição
      const formattedValue = formatPhone(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));

      // Se o número tiver pelo menos 10 dígitos, buscar cliente
      if (normalizedValue.length >= 10) {
        await searchCustomer(normalizedValue);
      } else {
        // Resetar status de cliente encontrado se o número for apagado
        setCustomerFound(false);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Buscar cliente pelo telefone
  const searchCustomer = async (phoneNumber) => {
    const phone = phoneNumber || normalizePhone(formData.phone);

    // Validar o telefone (deve ter pelo menos 10 dígitos)
    if (phone.length < 10) {
      return;
    }

    setIsSearchingCustomer(true);

    try {
      const customer = await getCustomerByPhone(phone);

      if (customer) {
        // Cliente encontrado, preencher os dados
        setFormData(prev => ({
          ...prev,
          name: customer.name || '',
          address: customer.address || '',
          number: customer.number || '',
          neighborhood: customer.neighborhood || '',
          reference: customer.reference || '',
          // Manter os valores selecionados para paymentMethod e deliveryOption
          paymentMethod: prev.paymentMethod,
          deliveryOption: prev.deliveryOption
        }));

        setCustomerFound(true);
      } else {
        // Cliente não encontrado
        setCustomerFound(false);
      }
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      setCustomerFound(false);
    } finally {
      setIsSearchingCustomer(false);
    }
  };

  // Construir a mensagem para o WhatsApp
  const buildWhatsAppMessage = () => {
    let message = '';

    // Título da mensagem com emojis e destaque
    const title = formData.deliveryOption === 'delivery'
      ? 'PEDIDO PARA ENTREGA'
      : 'PEDIDO PARA RETIRADA';

    message += `*${title}*\n\n`;

    // Número do pedido (usando timestamp como referência)
    const orderRef = new Date().getTime().toString().slice(-6);
    message += `*Pedido #${orderRef}*\n\n`;

    // Informações do cliente
    message += `*DADOS DO CLIENTE*\n`;
    message += `Nome: ${formData.name}\n`;
    message += `Telefone: ${formData.phone}\n\n`;

    // Endereço (apenas se for entrega)
    if (formData.deliveryOption === 'delivery') {
      message += `*ENDEREÇO DE ENTREGA*\n`;
      message += `Rua: ${formData.address}\n`;
      message += `Número: ${formData.number}\n`;

      if (formData.neighborhood) {
        message += `Bairro: ${formData.neighborhood}\n`;
      }

      if (formData.reference) {
        message += `Ponto de Referência: ${formData.reference}\n`;
      }

      message += `\n`;
    }

    // Data e hora do pedido
    const now = new Date();
    const formattedDate = now.toLocaleDateString('pt-BR');
    const formattedTime = now.toLocaleTimeString('pt-BR');
    message += `Data do pedido: ${formattedDate} às ${formattedTime}\n\n`;

    // Lista de produtos
    message += `*ITENS DO PEDIDO*\n`;

    // Itens do pedido
    let itemTotal = 0;
    cartItems.forEach((item, index) => {
      const price = item.salePrice && item.salePrice < item.price ? item.salePrice : item.price;
      const subtotal = price * item.quantity;
      itemTotal += subtotal;

      const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(price);

      const formattedSubtotal = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(subtotal);

      // Formatar nome do produto com limite de caracteres
      const productName = item.name.length > 25 ? `${item.name.substring(0, 25)}...` : item.name;

      message += `• ${productName}\n`;
      message += `  ${item.quantity}x ${formattedPrice} = ${formattedSubtotal}\n`;
    });

    // Resumo do pedido
    message += `\n*RESUMO DO PEDIDO*\n`;
    message += `Subtotal: ${formatPrice(itemTotal)}\n`;
    message += `Frete: ${formData.deliveryOption === 'delivery' ? 'Grátis' : 'N/A'}\n`;
    message += `TOTAL: ${formatPrice(getTotalPrice())}\n\n`;

    // Método de pagamento
    const paymentMethods = {
      pix: 'PIX',
      creditCard: 'Cartão de Crédito',
      bankTransfer: 'Transferência Bancária',
      cash: 'Dinheiro'
    };

    message += `*FORMA DE PAGAMENTO*\n`;
    message += `${paymentMethods[formData.paymentMethod] || 'Não informado'}\n\n`;

    // Mensagem de agradecimento e experiência do cliente
    message += `*Floratta Cosméticos*\n`;
    message += `Agradecemos pela sua preferência! Seu pedido foi recebido e está sendo processado com carinho. Qualquer dúvida, estamos à disposição.\n\n`;

    // Informações de contato da loja
    message += `Contato: (94) 98153-6324\n`;

    return encodeURIComponent(message);
  };

  // Manipular envio do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar formulário
    if (!formData.name || !formData.phone) {
      alert('Por favor, preencha seu nome e telefone.');
      return;
    }

    if (formData.deliveryOption === 'delivery' && (!formData.address || !formData.number || !formData.neighborhood)) {
      alert('Para entrega, é necessário informar rua, número e bairro.');
      return;
    }

    // Verificar se o método de pagamento foi selecionado
    if (!formData.paymentMethod) {
      setPaymentError(true);
      setHighlightPayment(true);
      // Remover o destaque após 5 segundos
      setTimeout(() => setHighlightPayment(false), 5000);
      return;
    }

    setIsSubmitting(true);

    try {
      // Normalizar o telefone para armazenamento
      const normalizedPhone = normalizePhone(formData.phone);

      // Salvar ou atualizar os dados do cliente no banco de dados
      await saveCustomer({
        phone: normalizedPhone,
        name: formData.name,
        address: formData.address,
        number: formData.number,
        neighborhood: formData.neighborhood,
        reference: formData.reference
      });

      // Incrementar o contador de pedidos para este cliente
      await incrementOrderCount(normalizedPhone);

      // Construir a URL do WhatsApp
      const message = buildWhatsAppMessage();
      const whatsappUrl = `https://wa.me/5594981536324?text=${message}`;

      // Gerar um número de pedido
      const orderNum = Math.floor(100000 + Math.random() * 900000);
      setOrderNumber(orderNum.toString());

      // Limpar o carrinho
      clearCart();

      // Marcar pedido como completo
      setOrderComplete(true);

      // Abrir WhatsApp em uma nova aba
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Erro ao processar pedido:', error);
      alert('Ocorreu um erro ao processar seu pedido. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Voltar para a página inicial
  const handleBackToHome = () => {
    navigate('/');
  };

  // Se o pedido foi concluído com sucesso, mostrar confirmação
  if (orderComplete) {
    return (
      <div className="container">
        <div className="checkout-success">
          <div className="success-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <h2>Pedido Realizado com Sucesso!</h2>
          <p>Seu número de pedido é: <strong>{orderNumber}</strong></p>
          <p>Você foi redirecionado para o WhatsApp para finalizar seu pedido.</p>
          <p>Se o WhatsApp não abriu automaticamente, <a href={`https://wa.me/5594981536324`} target="_blank" rel="noopener noreferrer">clique aqui</a>.</p>
          <button
            className="back-to-home-button"
            onClick={handleBackToHome}
          >
            Voltar para a Loja
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="checkout-page">
        <h1>Finalizar Compra</h1>

        <div className="checkout-container">
          <div className="checkout-form-container">
            <h2>Informações de Contato</h2>
            {customerFound && (
              <div className="customer-found-message">
                <i className="fas fa-user-check"></i>
                <p>Cliente encontrado! Seus dados foram preenchidos automaticamente.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="checkout-form">
              <div className="form-group">
                <label htmlFor="phone">Telefone / WhatsApp*</label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(94) 99999-9999"
                  required
                  ref={whatsappInputRef}
                />
                {customerFound && (
                  <div className="customer-found-badge">
                    <i className="fas fa-user-check"></i> Cliente encontrado
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="name">Nome Completo</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              {formData.deliveryOption === 'delivery' && (
                <>
                  <h2>Endereço de Entrega</h2>

                  <div className="form-group">
                    <label htmlFor="address">Rua*</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Nome da rua"
                      required={formData.deliveryOption === 'delivery'}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="number">Número*</label>
                      <input
                        type="text"
                        id="number"
                        name="number"
                        value={formData.number}
                        onChange={handleChange}
                        placeholder="Número"
                        required={formData.deliveryOption === 'delivery'}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="neighborhood">Bairro*</label>
                      <input
                        type="text"
                        id="neighborhood"
                        name="neighborhood"
                        value={formData.neighborhood}
                        onChange={handleChange}
                        placeholder="Seu bairro"
                        required={formData.deliveryOption === 'delivery'}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="reference">Ponto de Referência</label>
                    <input
                      type="text"
                      id="reference"
                      name="reference"
                      value={formData.reference}
                      onChange={handleChange}
                      placeholder="Próximo a..."
                    />
                  </div>
                </>
              )}

              <div className="option-section">
                <h3>Forma de Recebimento</h3>
                <div className="option-methods">
                  <div className="option-method">
                    <input
                      type="radio"
                      id="delivery"
                      name="deliveryOption"
                      value="delivery"
                      checked={formData.deliveryOption === 'delivery'}
                      onChange={handleChange}
                    />
                    <label htmlFor="delivery">
                      <i className="fas fa-truck"></i>
                      Entrega no endereço
                    </label>
                  </div>

                  <div className="option-method">
                    <input
                      type="radio"
                      id="pickup"
                      name="deliveryOption"
                      value="pickup"
                      checked={formData.deliveryOption === 'pickup'}
                      onChange={handleChange}
                    />
                    <label htmlFor="pickup">
                      <i className="fas fa-store"></i>
                      Retirar na loja
                    </label>
                  </div>
                </div>
              </div>

              <div className={`payment-methods ${paymentError ? 'payment-error' : ''} ${highlightPayment ? 'highlight' : ''}`}>
                <h3>Forma de pagamento <span className="required-field">*</span></h3>
                {paymentError && (
                  <div className="error-message">Por favor, selecione uma forma de pagamento</div>
                )}
                <div className="payment-method">
                  <input
                    type="radio"
                    id="pix"
                    name="paymentMethod"
                    value="pix"
                    checked={formData.paymentMethod === 'pix'}
                    onChange={handleChange}
                  />
                  <label htmlFor="pix">
                    <i className="fas fa-qrcode"></i>
                    PIX
                  </label>
                </div>

                <div className="payment-method">
                  <input
                    type="radio"
                    id="creditCard"
                    name="paymentMethod"
                    value="creditCard"
                    checked={formData.paymentMethod === 'creditCard'}
                    onChange={handleChange}
                  />
                  <label htmlFor="creditCard">
                    <i className="fas fa-credit-card"></i>
                    Cartão de Crédito
                  </label>
                </div>

                <div className="payment-method">
                  <input
                    type="radio"
                    id="bankTransfer"
                    name="paymentMethod"
                    value="bankTransfer"
                    checked={formData.paymentMethod === 'bankTransfer'}
                    onChange={handleChange}
                  />
                  <label htmlFor="bankTransfer">
                    <i className="fas fa-university"></i>
                    Transferência Bancária
                  </label>
                </div>

                <div className="payment-method">
                  <input
                    type="radio"
                    id="cash"
                    name="paymentMethod"
                    value="cash"
                    checked={formData.paymentMethod === 'cash'}
                    onChange={handleChange}
                  />
                  <label htmlFor="cash">
                    <i className="fas fa-money-bill-wave"></i>
                    Dinheiro
                  </label>
                </div>
              </div>

              <div className="checkout-actions">
                <button
                  type="button"
                  className="back-button"
                  onClick={() => navigate(-1)}
                >
                  Voltar
                </button>

                <button
                  type="submit"
                  className="complete-order-button"
                  disabled={isSubmitting || cartItems.length === 0}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Processando...
                    </>
                  ) : (
                    'Finalizar Pedido'
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="order-summary">
            <h2>Resumo do Pedido</h2>

            {cartItems.length === 0 ? (
              <div className="empty-cart-message">
                <p>Seu carrinho está vazio</p>
              </div>
            ) : (
              <>
                <div className="order-items">
                  {cartItems.map(item => (
                    <div key={item.code} className="order-item">
                      <div className="order-item-image">
                        <img
                          src={`http://localhost:3002/imagens/${item.code}.png`}
                          alt={item.name}
                          onError={(e) => {
                            e.target.onerror = null;

                            // Função para codificar corretamente strings Unicode para base64
                            const utf8ToBase64 = (str) => {
                              return window.btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
                                return String.fromCharCode('0x' + p1);
                              }));
                            };

                            // Usar apenas as primeiras palavras do nome para evitar textos muito longos
                            const shortName = item.name.split(' ').slice(0, 2).join(' ');

                            // Fallback para uma imagem SVG gerada com o nome do produto
                            const svgContent = `<svg width="50" height="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg"><rect width="50" height="50" fill="#f0f0f0"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="8" fill="#333">${shortName}</text></svg>`;
                            const svgImage = `data:image/svg+xml;base64,${utf8ToBase64(svgContent)}`;
                            e.target.src = svgImage;
                          }}
                        />
                      </div>
                      <div className="order-item-details">
                        <h3>{item.name}</h3>
                        <div className="order-item-price">
                          {item.quantity} x {item.salePrice && item.salePrice < item.price
                            ? formatPrice(item.salePrice)
                            : formatPrice(item.price)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-totals">
                  <div className="order-subtotal">
                    <span>Subtotal:</span>
                    <span>{formatPrice(getTotalPrice())}</span>
                  </div>
                  <div className="order-shipping">
                    <span>Frete:</span>
                    <span>Grátis</span>
                  </div>
                  <div className="order-total">
                    <span>Total:</span>
                    <span>{formatPrice(getTotalPrice())}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
